import type { DayId } from './days'
import type { DailyNutritionSummary, MealLogRow } from './mealLogCore'
import { summarizeDailyNutrition } from './mealLogCore'
import type { MealBlock } from './protocolTypes'
import { supabase } from './supabaseClient'

export type LogMealPayload = {
  log_date: string
  day_id?: DayId
  meal_block_name: string
  meal_protocol_id?: string | null
  event_type: 'prepared' | 'consumed' | 'skipped'
  source_type: 'protocol' | 'custom'
  dish_name: string
  calories_kcal?: number
  protein_g?: number
  carbs_g?: number
  fat_g?: number
  portion_pct?: number
  notes?: string | null
}

type LogMealResponse = {
  entry: MealLogRow | null
  summary: DailyNutritionSummary
  error?: string
}

export async function logMeal(payload: LogMealPayload): Promise<{ data: LogMealResponse | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke('log-meal', { body: payload })
  if (error) return { data: null, error: error.message }
  if (data?.error) return { data: null, error: String(data.error) }
  return { data: data as LogMealResponse, error: null }
}

export async function loadMealLogsForDate(userId: string, logDate: string): Promise<{ logs: MealLogRow[]; error: string | null }> {
  const { data, error } = await supabase
    .from('meal_log_entries')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', logDate)
    .order('logged_at', { ascending: true })

  if (error) return { logs: [], error: error.message }
  return { logs: (data ?? []) as MealLogRow[], error: null }
}

export function buildDailySummaryFromMeals(
  logDate: string,
  dayMeals: MealBlock[],
  logs: MealLogRow[],
): DailyNutritionSummary {
  const planned = dayMeals.map((m) => ({
    calories_kcal: m.calories_kcal,
    protein_g: m.protein_g,
    carbs_g: m.carbs_g,
    fat_g: m.fat_g,
  }))
  return summarizeDailyNutrition(logDate, planned, logs, dayMeals.length)
}

export function mealToLogPayload(
  meal: MealBlock,
  logDate: string,
  eventType: 'prepared' | 'consumed',
  portionPct = 100,
): LogMealPayload {
  const scale = portionPct / 100
  return {
    log_date: logDate,
    day_id: meal.day_id,
    meal_block_name: meal.block_name,
    meal_protocol_id: meal.id,
    event_type: eventType,
    source_type: 'protocol',
    dish_name: meal.dish_name,
    calories_kcal: Math.round(meal.calories_kcal * scale),
    protein_g: Math.round(meal.protein_g * scale),
    carbs_g: Math.round(meal.carbs_g * scale),
    fat_g: Math.round(meal.fat_g * scale),
    portion_pct: portionPct,
  }
}
